import { Component, ElementRef, HostListener, Input, OnInit, ViewChild } from "@angular/core";
import { ChartConfiguration } from "chart.js";
import moment from "moment";
import { BaseChartDirective } from "ng2-charts";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";
import { ModuleThemesService } from "src/app/reusables/services/module-themes.service";
import { NumericType } from "src/shared/models/metric";
import { ModuleManager } from "src/shared/models/module-manager";
import { IModuleManagerMenuItem, ModuleManagerMenuItemType, ModuleManagerSubMenu, } from "src/shared/models/module-manager/module-manager-menu-item.model";
import { ModuleManagerRefreshType } from "src/shared/models/module-manager/module-manager-options.model";
import { TimeInterval } from "src/shared/models/module-manager/module-manager-settings.models";
import { ReportSelectorSettings } from "src/shared/models/pipeline-config/reporting-sets.models";
import { CoachingEffectivenessData, CoachingSessionGraphItem, } from "./coaching-effectiveness.model";
import { CoachingEffectivenessService } from "./coaching-effectiveness.service";

@Component({
    selector: "coaching-effectiveness",
    templateUrl: "./coaching-effectiveness.component.html",
    styleUrl: "./coaching-effectiveness.component.scss",
})
export class CoachingEffectivenessComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @ViewChild(BaseChartDirective) chartObject: BaseChartDirective;
    @ViewChild('legendTooltip') legendTooltip: ElementRef;


    public data: CoachingEffectivenessData;
    public dataLoaded = false;
    public isLoadingData = false;
    public noData = false;
    public noDataLocalization = "";

    // Chart.js
    public chartConfig = null;
    public legendItemsHidden = new Map<number, boolean>();
    public chartAxisShowStates = {};
    //Chart module state
    public chartState = {
        //State object properties
        legendPosition: "top",
        themeIndex: 0,
        name: "chartState",
        autoLoad: true,
        showObjective: false,
    };
    public zoom: any = {
        max: 2.5,
        min: 1,
        cur: 1,
        baseTicks: 5,
    };
    public isHoveringOverChart = false;
    //Color theme handling
    public currentThemeIndex =
        this.chartState.themeIndex != null ? this.chartState.themeIndex : 0;
    public currentTheme = null;
    public themeMenuOptions = [];
    // Legend Position
    public chartLegendPosition =
        this.chartState.legendPosition != null
            ? this.chartState.legendPosition
            : "top";
    private chartPadding = {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
    };
    private chartBackgroundColor = {
        id: 'custom_canvas_background_color',
        beforeDraw: (chart) => {
            const ctx = chart.canvas.getContext('2d');
            ctx.save();
            ctx.globalCompositeOperation = 'destination-over';
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, chart.canvas.width, chart.canvas.height);
            ctx.restore();
        }
    };
    private firstAnimateComplete = false;
    private isHoveringOverSession = false;
    private coachingSessionMap = new Map<number, CoachingSessionGraphItem[]>();
    private currentSessionIndex = -1;
    private labelHasText = false;
    private dateRangeForLocalization = "No_Session_Last_30_Days";
    private tooltipLegendItems = new Map<number, string>();
    private dayInterval = false;

    private stateSaveTimeout: NodeJS.Timeout; // For debouncing calls to queueStateSave().

    constructor(
        private service: CoachingEffectivenessService,
        private cvLocalizationService: CVLocalizationService,
        private moduleThemesService: ModuleThemesService,
    ) {}

    @HostListener('wheel', ['$event']) onMouseWheel(event: WheelEvent) {
        if (this.isHoveringOverChart) {
            this.handleZoom(event.deltaY);
            event.preventDefault(); // Prevent the default behavior only when handling zoom
        }
    };

    async ngOnInit() {
        // These settings filter unneeded group levels in the slideout
        const reportSelectorSettings: ReportSelectorSettings = {
            HasEmpSelector: true,
            EmpSelectorLevels: [1],
            HasRecSelector: false,
            RecSelectorLevels: [],
            HasDateRangeSelector: false,
            ExtraElements: [],
        };
        this.moduleManager.updateWidgetSettings(reportSelectorSettings);

        await this.moduleManager.setMaxDateRange(92); // The max date range filters out Year date ranges

        this.moduleManager.setOptions({
            hasRefresh: true,
            canRefresh: () => !this.isLoadingData,
            onRefresh: (isAutoRefresh) => this.loadData(isAutoRefresh),
            onExport: (format, isCXOne) => this.service.exportData(this.moduleKey, this.moduleManager.container.moduleInfo.DTO.ModuleID),
            onImageExport: (isCXOne) => this.exportPNG(),
            imageExportVisible: () => this.dataLoaded && !this.noData,
            refreshType: ModuleManagerRefreshType.FifteenMinute,
            onSettingsApplied: () => this.loadData(false, true), // This is called when the date range selector dropdown in the top-left is changed.  Force a refresh, in case the user is rapidly changing between date ranges.
        });

        this.themeMenuOptions = this.moduleThemesService.GetModuleThemesMenu();
        this.setThemeClick();
        this.moduleManager.addState({
            name: "chartState",
            autoLoad: true,
            getStateData: () => this.chartState,
            onStateLoaded: (stateData) => {
                if (!stateData) {return;}
                this.chartState = stateData;
                this.currentThemeIndex = this.chartState.themeIndex != null
                    ? this.chartState.themeIndex
                    : 0;
                this.currentTheme = this.moduleThemesService.ModuleThemes[this.currentThemeIndex].Colors;
                this.setLegendPosition(this.chartState.legendPosition);
            },
        });

        const localizationPromises = [
            this.cvLocalizationService.GetSection("Coaching_Effectiveness"),
            this.cvLocalizationService.GetSection("Common"),
        ];
        await Promise.all(localizationPromises);
    }

    public async loadData(isAutoRefresh = false, isFirstLoad = false) {
        if (this.isLoadingData) return;

        if (!isAutoRefresh) this.moduleManager.showLoader = true;

        this.isLoadingData = true;

        try {
            this.data = await this.service.loadData(this.moduleKey, isAutoRefresh,isFirstLoad);
            if (!this.data ||
                !this.data.CoachingSessionGraphItems ||
                this.data.CoachingSessionGraphItems.length === 0) {
                this.noData = true;
                this.dateRangeForLocalization = "No_Session_"
                    + this.moduleManager.moduleSettings.selectedDateRange.name.replaceAll(" ","_");
                this.noDataLocalization = this.cvLocalizationService.getCachedText("Coaching_Effectiveness", this.dateRangeForLocalization,
                    "No sessions found in the specificied date range");
            } else {
                this.noData = false;
                this.createChart();
            }

            this.dataLoaded = true;
        } catch (err) {
            console.error(err);
        } finally {
            this.moduleManager.showLoader = false;
            this.moduleManager.container.showDateRange = true;
            // No graph title implies no agent selected
            if(this.data?.GraphTitle)
                this.moduleManager.setAutoRefreshReady();
            this.isLoadingData = false;
            this.moduleManager.resetMenu();
            this.addMenuItemsChart();
        }
    }

    // Supports either Metric or Graph Items
    public getMetricDisplayName(metricElement) {
        if (metricElement.MetricCustomName && metricElement.MetricCustomName !== "")
            return metricElement.MetricCustomName + "*";

        return metricElement?.Abbreviation ?? metricElement.MetricName;
    }

    public addMenuItemsChart() {
        // Themes
        this.moduleManager.addMenuItem({
            name: "Themes",
            icon: "palette",
            type: ModuleManagerMenuItemType.SubMenu,
            subItems: this.themeMenuOptions,
            getEnabled: () => this.chartObject != null && this.data?.Metrics?.length > 0,
        } as ModuleManagerSubMenu);

        // Legend Position
        this.moduleManager.addMenuItem({
            name: "Legend_Position",
            icon: "legend",
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => this.chartObject != null && this.data?.Metrics?.length > 0,
            subItems: [
                {
                    name: this.cvLocalizationService.getCachedText(
                        "Coaching_Effectiveness",
                        "Top",
                        "Top"
                    ),
                    icon: "positionTop",
                    click: () => this.setLegendPosition("top"),
                },
                {
                    name: this.cvLocalizationService.getCachedText(
                        "Coaching_Effectiveness",
                        "Bottom",
                        "Bottom"
                    ),
                    icon: "positionBottom",
                    click: () => this.setLegendPosition("bottom"),
                },
                {
                    name: this.cvLocalizationService.getCachedText(
                        "Coaching_Effectiveness",
                        "Left",
                        "Left"
                    ),
                    icon: "positionLeft",
                    click: () => this.setLegendPosition("left"),
                },
                {
                    name: this.cvLocalizationService.getCachedText(
                        "Coaching_Effectiveness",
                        "Right",
                        "Right"
                    ),
                    icon: "positionLeft",
                    click: () => this.setLegendPosition("right"),
                },
            ],
        } as ModuleManagerSubMenu);

        // Metrics
        this.moduleManager.addMenuItem({
            name: this.cvLocalizationService.getCachedText(
                "Coaching_Effectiveness",
                "Metrics",
                "Metrics"
            ),
            icon: "metrics",
            getEnabled: () =>
                !this.noData &&
                this.data?.Metrics?.length > 0 &&
                this.chartObject != null,
            type: ModuleManagerMenuItemType.CheckMenu,
            getCheckItems: () => this.data.Metrics,
            getItemName: (i) => this.getMetricDisplayName(this.data.Metrics[i]),
            isItemChecked: (i) => this.legendItemsHidden.get(this.data.Metrics[i].MetricID) === false,
            onCheckClicked: (i) => this.metricMenuClickHandler(this.data.Metrics[i].MetricID),
        } as IModuleManagerMenuItem);
    }

    public async setLegendPosition(position: string) {
        //Set position string
        this.chartLegendPosition = position;
        this.chartState.legendPosition = position;

        //Set padding values
        switch (position) {
            case "top": //Default view, Chart.js defaults all padding values to 0.
                this.chartPadding = {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                };
                break;
            case "bottom": //Extra top space. Bottom is padded by the legend, so no extra bottom padding.
                this.chartPadding = {
                    top: 16,
                    bottom: 0,
                    left: 0,
                    right: 0,
                };
                break;
            case "left": //Extra top and bottom space
                this.chartPadding = {
                    top: 16,
                    bottom: 8,
                    left: 0,
                    right: 0,
                };
                break;
            case "right": //Extra top and bottom space
                this.chartPadding = {
                    top: 16,
                    bottom: 8,
                    left: 0,
                    right: 0,
                };
                break;
            default: //Same as 'top'
                this.chartPadding = {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                };
                break;
        }

        // Moving the legend requires the chart to be completely recreated instead of just updated.
        if(this.dataLoaded)
        {
            this.createChart();
            this.chartObject.render();
            this.queueStateSave();
        }
    }

    public createChart() {
        const preppedDatasets = [];
        const seriesTypes = [];
        let xAxisLabels = [];
        const preparedYAxes = {};
        const transparentColor = "rgba(0,0,0,0)"; //For the Alpha=0 value. Draws the border (which allows the under-the-line fill) but sets the actual line to be transparent.
        let colorIndex = 0;
        this.dayInterval = this.data.IntervalType === TimeInterval.Day;
        this.zoom.cur = 1;

        // Prep the x-axis labels
        xAxisLabels = this.data?.IntervalGroups;

        for (let i = 0; i < xAxisLabels?.length; i++) {
            xAxisLabels[i] = moment(xAxisLabels[i], CVMomentDateFormat).format(CVMomentMonthDayFormat);
        }

        // Coaching Session data
        // Keep at index 0, chartJS draws in reverse order and some logic depends on it
        const coachingData = [];
        const coachingDisplay = [];
        this.coachingSessionMap.clear();
        this.data.CoachingSessionGraphItems.forEach((coachingSession) => {
            if (coachingSession.xTick < 0) return;
            if (this.coachingSessionMap.has(coachingSession.xTick))
                this.coachingSessionMap.get(coachingSession.xTick).push(coachingSession);
            else
                this.coachingSessionMap.set(coachingSession.xTick, [coachingSession]);

            coachingData.push({
                x: Math.floor(coachingSession.xTick),
                y: 0,
                xTick: coachingSession.xTick,
            });

            coachingDisplay.push(coachingSession.Name);
        });

        const coachingSessionDataset = {
            xAxisID: "x",
            yAxisID: "yCoaching",
            label: "Coaching Sessions",
            type: "scatter",
            data: coachingData,
            displayValues: coachingDisplay,
            backgroundColor: "rgba(32, 103, 218, 1)",
            borderColor: "rgba(32, 103, 218, 1)",
            dataType: NumericType.Number,
            fill: "origin",
            hitRadius: 0,
            hoverRadius: 7,
            pointRadius: 6,
            pointBorderColor: "rgba(32, 103, 218, 1)",
            pointBackgroundColor: "rgba(32, 103, 218, 1)",
            hidden: false,
        };

        preppedDatasets.push(coachingSessionDataset);

        Object.assign(preparedYAxes, {
            ["yCoaching"]: {
                id: "yCoaching",
                display: false,
                min: 0,
                max: 1,
                ticks: {
                    display: false,
                },
            },
        });

        // Moves coaching sessions to the correct x-axis position
        const offsetCoachingSessions = {
            id: "offset_coaching_sessions",
            beforeDraw: (chart) => {
                if(this.dayInterval)
                    return;
                this.adjustCoachingSessionPosition(chart);
            },
        };

        this.tooltipLegendItems.clear();
        //Create a chart series for each dataset.
        this.data.GraphItems?.forEach((element, index) => {
            if(!this.legendItemsHidden.has(element.MetricID))
                this.legendItemsHidden.set(element.MetricID, false);
            // Create Y-Axes
            const yAxisId = "YAxisType" + element.NumericType;

            // Check if a Y-Axis for this NumericType already exists, creating a new one if not.
            if (seriesTypes.indexOf(element.NumericType) < 0) {
                this.chartAxisShowStates[yAxisId] = 1;
                seriesTypes.push(element.NumericType);

                //Check for Type 3 (Time) metrics. The scale requires special axis-tick formatting. If detected, push an axis with the right configuration. Othewise, use the default.
                if (element.NumericType === 3) {
                    Object.assign(preparedYAxes, {
                        [yAxisId]: {
                            id: yAxisId,
                            display: true,
                            position:
                                seriesTypes.length <= 2 ? "left" : "right", //4 total types (enums: 0,1,2,3). Put the third and fourth types' axes on the right.
                            title: {
                                display: true,
                                font: {
                                    weight: "bold",
                                },
                                text: element.NumericTypeName,
                            },
                            ticks: {
                                //"2018-01-01" is arbitrary. We just need a valid date object to allow us to convert a total-seconds value to HH:MM:SS formatting.
                                callback: (value) =>
                                    moment("2018-01-01")
                                        .startOf("day")
                                        .seconds(value)
                                        .format("HH:mm:ss")
                                        .toString(),
                                autoSkip: true,
                                maxTicksLimit: Math.round(this.zoom.baseTicks * this.zoom.cur),
                                minRotation: 0,
                                maxRotation: 0,
                            },
                        },
                    });
                } else {
                    //Default axis config
                    Object.assign(preparedYAxes, {
                        [yAxisId]: {
                            id: yAxisId,
                            display: true,
                            position:
                                seriesTypes.length <= 2 ? "left" : "right", //4 total types (enums: 0,1,2,3). Put the third and fourth types' axes on the right
                            title: {
                                display: true,
                                font: {
                                    weight: "bold",
                                },
                                text: element.NumericTypeName,
                            },
                        },
                    });
                }
            } else {
                this.chartAxisShowStates[yAxisId] += 1;
            }

            //Ensure a theme exists
            if (this.currentTheme == null) {
                this.currentTheme = this.moduleThemesService.ModuleThemes[0].Colors;
            }

            //Add the series to be displayed
            preppedDatasets.push({
                xAxisID: "x",
                yAxisID: yAxisId,
                label: element.MetricCustomName && element.MetricCustomName !== ""
                    ? element.MetricCustomName + "*"
                    : element.MetricName,
                backgroundColor: this.currentTheme[colorIndex],
                borderColor: this.data.GraphItems.length > 5
                    ? this.currentTheme[colorIndex]
                    : transparentColor,
                data: element.MetricValues,
                displayValues: element.MetricDisplayValues,
                dataType: element.NumericType, //Based on framework type: ClearView.Quality.Metrics.Enums.NumericType
                fill: this.data.GraphItems.length > 5 ? false : "origin",
                hitRadius: 2,
                hoverRadius: 5,
                pointRadius: 4,
                pointBorderColor: this.currentTheme[colorIndex],
                pointBackgroundColor: this.currentTheme[colorIndex],
                hidden: this.legendItemsHidden.get(element.MetricID),
                metricID: element.MetricID,
            });
            if (this.legendItemsHidden.get(element.MetricID)) { // if it has been previously hidden update the count of metrics tied to specific y-axis
                this.chartAxisShowStates[yAxisId] -= 1;
            }

            // Color Picker
            colorIndex++;
            if (colorIndex >= this.currentTheme.length) {
                colorIndex = 0;
            }

            // Adds an entry that maps the index of a renamed metric
            if(element.MetricCustomName && element.MetricCustomName !== "")
            {
                this.tooltipLegendItems.set(index, this.cvLocalizationService.getCachedText(
                    "Common", "Original_Metric", "Original Metric Name") + ": " + "\n" + element.MetricName);
            }
        }
        );
        this.verifyYAxisDisplay(preparedYAxes);

        this.chartLegendPosition = this.chartState.legendPosition != null
            ? this.chartState.legendPosition
            : "top";

        //Create the chart
        let hovering = false;
        this.chartConfig = {
            type: "line",
            data: {
                datasets: preppedDatasets,
                labels: xAxisLabels.map((label, index) => index),
            },
            options: {
                // Please keep option keys in alphabetical order for future searchability

                /* --- Single Value Options --- */
                clip: false, //Allows the graph to draw outside of the canvas area.
                maintainAspectRatio: false, //Disable to keep responsiveness, but stop pushing data off the bottom of the module pane
                responsive: true,
                spanGaps: false, //Tells Chart.js to not draw lines between points when data is NaN/null.

                /* --- Object Based Options --- */
                animation: {
                    duration: !this.firstAnimateComplete
                        ? 1000
                        : 0 /* If the first animation has not run, use the Chart.js default 1000, else set it to 0. */,
                },
                elements: {
                    line: {
                        tension: 0.3,
                    },
                },
                indexAxis: "x",
                interaction: {
                    intersect: true,
                    mode: 'x',
                },
                layout: {
                    padding: this.chartPadding,
                },
                // Zoom needs to know when we are hovering over chart
                onHover: (event, chartElement) => this.isHoveringOverChart = true,
                onLeave: () => this.isHoveringOverChart = false,
                plugins: {
                    legend: {
                        onClick: this.legendClickHandler.bind(this),
                        position: this.chartLegendPosition,
                        labels: {
                            boxWidth: 16,
                            filter: (item, chart) =>
                                item.text !== "Coaching Sessions",
                        },
                        onHover: (event, legendItem) => {
                            if (hovering || !this.tooltipLegendItems.has(legendItem.datasetIndex - 1)) {
                                return;
                            }
                            hovering = true;
                            this.legendTooltip.nativeElement.innerHTML = this.tooltipLegendItems.get(legendItem.datasetIndex - 1);
                            this.legendTooltip.nativeElement
                                .setAttribute('style', `left:${event.x + 15}px; top:${event.y}px; visibility:visible`);
                        },
                        onLeave: () => {
                            hovering = false;
                            this.legendTooltip.nativeElement.innerHTML = "";
                            this.legendTooltip.nativeElement.setAttribute('style', `visibility:hidden`);
                        },
                    },
                    title: {
                        display: true,
                        text: this.data.GraphTitle,
                    },
                    tooltip: {
                        animation: false,
                        intersect: true,
                        mode: "index",
                        position: "nearest",
                        padding: 6,
                        usePointStyle: true,
                        boxPadding: 1,
                        boxWidth: 18,
                        boxHeight: 18,
                        borderColor: "#353a40",
                        titleFont: {
                            size: 16,
                            weight: "bold",
                        },
                        titleColor: "#fff",
                        bodyColor: "#fff",
                        footerColor: "#fff",
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        bodyFont: {
                            size: 14,
                        },
                        footerFont: {
                            size: 14,
                            weight: "normal",
                        },
                        borderWidth: 0,
                        cornerRadius: 6,
                        callbacks: {
                            // Runs first and checks if the user is hovering over a coaching session.
                            beforeTitle: (tooltipItems) => {
                                if(tooltipItems[0].dataset.label === "Coaching Sessions"
                                    && (tooltipItems[0].element.active
                                    || tooltipItems.length === 1
                                    || tooltipItems.findIndex(item => item?.element?.active === true) === -1))
                                {
                                    this.isHoveringOverSession = true;
                                    const element = tooltipItems[0].element as any;
                                    this.currentSessionIndex = element.$context.raw.xTick;
                                    return;
                                }
                                else if(this.dayInterval && tooltipItems.length > 2){
                                    const element = tooltipItems[1] as any;
                                    if(element.parsed.x === tooltipItems[1].dataIndex
                                        && this.coachingSessionMap.has(element.parsed.x))
                                    {
                                        this.isHoveringOverSession = true;
                                        this.currentSessionIndex = element.parsed.x;
                                        return;
                                    }
                                }
                                this.isHoveringOverSession = false;
                            },
                            // tooltipItemArray is an array of all Y-values for the given X-value/position being hovered over.
                            title: (tooltipItemArray) => {
                                if (this.isHoveringOverSession) {
                                    const session = this.coachingSessionMap.get(this.currentSessionIndex);
                                    return session[0].CreatedDateDisplay;
                                }
                                else
                                    return xAxisLabels[tooltipItemArray[0].dataIndex];
                            },
                            label: (tooltipItem) => {
                                if (this.isHoveringOverSession) {
                                    const session = this.coachingSessionMap.get(this.currentSessionIndex);
                                    const date = session[0].CreatedDateDisplay;
                                    if (!this.data.IntervalGroups.includes(date)) {
                                        this.labelHasText = false;
                                        return null;
                                    }
                                }

                                if (tooltipItem.dataset.label !== "Coaching Sessions") {
                                    const indexFloored = Math.floor(this.currentSessionIndex);
                                    // Impossible to have metric data on a non integer index
                                    if(this.isHoveringOverSession && indexFloored !== this.currentSessionIndex)
                                    {
                                        this.labelHasText = false;
                                        return null;
                                    }
                                    // Null metric data will sometimes return the wrong index if they're being called from a coaching session.
                                    const index = this.isHoveringOverSession ? indexFloored : tooltipItem.dataIndex;
                                    const displayValue = (tooltipItem.dataset as any).displayValues[index];
                                    this.labelHasText = displayValue && displayValue !== "";
                                    return this.labelHasText ? tooltipItem.dataset.label + ": " +
                                    displayValue : null;
                                }
                                this.labelHasText = false;
                                return null;
                            },
                            labelColor: (tooltipItem) => ({
                                borderColor: tooltipItem.dataset.borderColor,
                                backgroundColor:
                                    tooltipItem.dataset.backgroundColor,
                                borderWidth: 0.5,
                            }),
                            labelPointStyle: (tooltipItem) => ({
                                pointStyle: "rect",
                                rotation: 0,
                            }),
                            footer: (tooltipItemArray) => {
                                if (!this.isHoveringOverSession ||
                                    this.currentSessionIndex <= -1)
                                    return [];
                                const footerText = [];
                                const sessions = this.coachingSessionMap.get(this.currentSessionIndex);

                                if (sessions?.length > 0) {
                                    if (this.labelHasText)
                                        footerText.push("");
                                    sessions.forEach((session, index) => {
                                        footerText.push(session.Name);
                                        session.Metrics?.forEach((metric) => {
                                            footerText.push(`  • ${this.getMetricDisplayName(metric)}`);});

                                        if (index < sessions.length - 1)
                                            footerText.push("");
                                    });
                                }
                                return footerText;
                            },
                        },
                    },
                },
                scales: {
                    ...preparedYAxes,
                },
            },
            plugins: [ offsetCoachingSessions, this.chartBackgroundColor ],
        } as ChartConfiguration;

        const scale = {
            display: true,
            distribution: "series",
            scaleLabel: {display: false,},
            ticks: {
                autoSkip: true,
                source: "labels",
                maxRotation: 0,
                callback: (value, index, ticks) => xAxisLabels[index],
            },
        };
        this.chartConfig.options.scales.x = scale;
        this.firstAnimateComplete = true;
        this.handleZoom();
    }

    public legendClickHandler(e, item) {
        const index = item.datasetIndex;
        const meta = this.chartObject?.chart.getDatasetMeta(index) as any;
        const metricID = meta._dataset.metricID;
        meta.hidden = !this.legendItemsHidden.get(metricID);

        this.hideMetric(metricID, meta.yAxisID, meta.hidden);
    }

    public metricMenuClickHandler(metricID: number) {
        const meta = this.chartObject?.chart.getDatasetMeta(
            this.data.GraphItems.findIndex((x) => x.MetricID === metricID) + 1);
        meta.hidden = !this.legendItemsHidden.get(metricID);

        this.hideMetric(metricID, meta.yAxisID, meta.hidden);
    }

    public hideMetric(metricID: number, yAxisID: string, hidden: boolean) {
        this.legendItemsHidden.set(metricID, hidden);

        if (hidden) {
            this.chartAxisShowStates[yAxisID] -= 1;
            if (this.chartAxisShowStates[yAxisID] === 0) {
                this.chartObject.chart.options.scales[yAxisID].display = false;
            }
        } else {
            this.chartAxisShowStates[yAxisID] += 1;
            this.chartObject.chart.options.scales[yAxisID].display = true;
        }
        this.chartObject.update();
        this.adjustCoachingSessionPosition(this.chartObject.chart);
    }

    public setThemeClick() {
        //Set the index, get the theme, update the chart
        for (const theme of this.themeMenuOptions) {
            theme.click = () => {
                this.currentThemeIndex = theme.themeIndex;
                this.currentTheme = this.moduleThemesService.GetThemeByIndex(this.currentThemeIndex);
                this.createChart();
                this.chartObject.render();

                //Update the state
                this.chartState.themeIndex = this.currentThemeIndex;
                this.queueStateSave();
            };
        }
    }

    public verifyYAxisDisplay(preparedYAxes) {
        for (const key in preparedYAxes) {
            if (this.chartAxisShowStates[key] === 0) {
                preparedYAxes[key].display = false;
            }
        }
    }

    /**
     * Updates the zoom level based on the deltaY value.
     * @param deltaY - The change in the Y direction.
     */
    public updateZoomLevel(deltaY: number): void {
        const temp = this.zoom.cur + 0.1 * ((deltaY > 0) ? -1 : (deltaY < 0) ? 1 : 0);
        if (temp > this.zoom.max) {
            this.zoom.cur = this.zoom.max;
        } else if (temp < this.zoom.min) {
            this.zoom.cur = this.zoom.min;
        } else {
            this.zoom.cur = temp;
        }
    }

    /**
     * Sets the max width based on current zoom
     */
    public updateChartDimensionsAndTicks(): void {
        const axis = 'x';
        const dimension = 'width';
        this.chartObject.chart.canvas.parentElement.style[dimension] = `max(100%,${100 * this.zoom.cur}%)`;
        this.chartConfig.options.scales[axis].ticks.maxTicksLimit = Math.round(this.zoom.cur * this.zoom.baseTicks);
    }

    /**
     * Handles the zoom functionality
     * @param deltaY - The change in the Y direction for zooming.
     */
    public handleZoom(deltaY: number = 0): void {
        if (!this.data || !this.chartObject) {
            return;
        }

        this.updateZoomLevel(deltaY);
        this.updateChartDimensionsAndTicks();
        this.chartObject.chart.update();
    }

    /**
     * Export the chart as a PNG file.
     */
    public exportPNG(){
        // toBase64Image() returns a png data url of the image on the canvas. (Sourced: https://www.chartjs.org/docs/latest/developers/api.html)
        // name of the file
        const date = (new Date()).toString('yyyy_MM_dd_HH_mm_ss');
        const fileName = this.cvLocalizationService.getCachedText("Module_Names","Coaching Effectiveness","Coaching Effectiveness")
            .replace(/\s+/g, '-').toLowerCase() + '_widget_(' + date + ').png';

        // create fake link
        const link = document.createElement('a');
        link.setAttribute('download', fileName);
        link.setAttribute('href', this.chartObject?.chart.toBase64Image());

        // start download
        this.clickExportLink(link);

        // cleanup
        URL.revokeObjectURL(link.href);
        link.remove();
    }

    /**
     * Export click in a separate function to avoid triggering an export in tests.
     */
    public clickExportLink(link: HTMLAnchorElement) {
        link.click();
    }

    /**
     * Saves the state.
     */
    public queueStateSave() {
        if (this.stateSaveTimeout) {
            clearTimeout(this.stateSaveTimeout);
        }
        this.stateSaveTimeout = setTimeout(async () => {
            this.stateSaveTimeout = null;
            if (await this.moduleManager.saveState()) {
                this.moduleManager.requestPublishNeeded();
            }
        }, 500);
    }

    /**
     * Adjusts the x position of coaching sessions if they are in between data ticks
     */
    private adjustCoachingSessionPosition(chart){
        const meta = chart.getDatasetMeta(0);
        const unit = this.data?.IntervalGroups.length <= 2
            ? meta.xScale.width
            : meta.xScale.width / (this.data?.IntervalGroups.length - 1);
        meta.data.forEach((element: any) => {
            const rawData = element.$context.raw as any;
            element.x = rawData.xTick * unit + meta.xScale.left;
        });
    }
}
